import React from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CadPollState } from '../../hooks/useCadRecord';
import { CallAttachment, isRescoredBySystem } from '../../types/Call';
import { SectionLabel, StageWaitBanner, StageWaitWording, StatusBanner } from './CadRecordParts';
import {
  RESCORE_READINESS_NOTE,
  RESCORE_WORDING,
  describeRescoreLanded,
  rescoreButtonLabel,
} from './cadRecordDisplay';

/** The section heading; the tab moves focus here when the button that started a rescore unmounts. */
export const RESCORE_HEADING_ID = 'cad-rescore-heading';

const RESCORE_WAIT: StageWaitWording = {
  queued: RESCORE_WORDING.queued,
  running: RESCORE_WORDING.running,
  body: 'The Analysis tab updates automatically when the score lands, usually within a minute.',
  stillTitle: 'Still scoring',
  stillBody: 'The rescore has taken more than 5 minutes. It is still queued on the server.',
  unavailableBody: 'The rescore status could not be read. Refresh the page or sign in again.',
};

interface CadRescoreSectionProps {
  record: CallAttachment;
  canEdit: boolean;
  /** The call has a completed analysis to merge into; without one the request would be refused. */
  canRescore: boolean;
  pollState: CadPollState;
  requesting: boolean;
  onRequest: () => void;
  onResume: () => void;
}

/**
 * The CAD Skills rescore (leg 6) on the tab: what it does, the wait on it, why it failed, and
 * when it last landed. The scores themselves show on the Analysis tab, where the category carries
 * the "Scored against CAD record" label; this section owns the request and its state, as the
 * verification list owns the check's.
 */
export const CadRescoreSection: React.FC<CadRescoreSectionProps> = ({
  record,
  canEdit,
  canRescore,
  pollState,
  requesting,
  onRequest,
  onResume,
}) => {
  const requestButton = (label: string) =>
    canEdit &&
    canRescore && (
      <Button
        variant="outlined"
        size="small"
        onClick={onRequest}
        disabled={requesting}
        startIcon={requesting ? <CircularProgress size={14} aria-hidden /> : undefined}
      >
        {label}
      </Button>
    );

  const readiness = canEdit && !canRescore ? ` ${RESCORE_READINESS_NOTE}` : '';

  const renderBody = () => {
    switch (record.rescoreStatus) {
      case 'NotRun':
        return (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" mb={canEdit && canRescore ? 1.5 : 0}>
              {canEdit
                ? `Score the CAD Skills category against the CAD record instead of the audio alone. The overall score and the review status follow; criteria with a manual score keep it.${readiness}`
                : 'CAD Skills has not been scored against the CAD record.'}
            </Typography>
            {requestButton(rescoreButtonLabel(record))}
          </Paper>
        );

      case 'Pending':
      case 'Rescoring':
        return (
          <StageWaitBanner
            queued={record.rescoreStatus === 'Pending'}
            pollState={pollState}
            wording={RESCORE_WAIT}
            onResume={onResume}
          />
        );

      case 'RescoreFailed':
        return (
          <StatusBanner
            tone="error"
            title={RESCORE_WORDING.failed}
            action={requestButton('Try again')}
          >
            {record.rescoreError || 'Rescoring failed.'}
            {readiness}
          </StatusBanner>
        );

      case 'Rescored':
        return (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" mb={canEdit && canRescore ? 1.5 : 0}>
              {describeRescoreLanded(record)}
              {isRescoredBySystem(record)
                ? ', done automatically'
                : record.rescoreRequestedBy
                  ? ` at the request of ${record.rescoreRequestedBy}`
                  : ''}
              . The Analysis tab shows the category with a &ldquo;Scored against CAD record&rdquo; label.
              {readiness}
            </Typography>
            {requestButton(rescoreButtonLabel(record))}
          </Paper>
        );
    }
  };

  return (
    <Stack spacing={1.5} component="section" aria-labelledby={RESCORE_HEADING_ID}>
      <SectionLabel id={RESCORE_HEADING_ID} focusable>
        CAD Skills score
      </SectionLabel>
      {renderBody()}
    </Stack>
  );
};
